/**
 * Client-side error reporter.
 *
 * Sends structured errors to the backend POST /api/client-errors endpoint
 * (authenticated — reports without a logged-in session stay in the queue).
 * Includes localStorage queue for offline retry and a per-session report limit.
 */

import type { StructuredError } from './ErrorLogger';
import { accessTokenStore } from '../auth/accessTokenStore';

const STORAGE_KEY = 'planback_error_queue';
const MAX_REPORTS_PER_SESSION = 20;
const QUEUE_FLUSH_INTERVAL_MS = 30_000; // retry queued errors every 30s
const MAX_QUEUE_SIZE = 50;

let sessionReportCount = 0;
let flushTimerId: ReturnType<typeof setInterval> | null = null;

function getApiBaseUrl(): string {
  if (import.meta.env.MODE === 'development') {
    return 'http://localhost:7777';
  }
  return import.meta.env.VITE_API_URL || window.location.origin;
}

interface ErrorReportPayload {
  severity: string;
  category: string;
  message: string;
  stackTrace?: string;
  url?: string;
  component?: string;
  browser: string;
  browserName?: string;
  browserVersion?: string;
  os?: string;
  screenResolution?: string;
  username?: string;
  tenantName?: string;
  additionalData?: string;
}

function parseBrowserInfo(): { browserName: string; browserVersion: string; os: string } {
  const ua = navigator.userAgent;
  let browserName = 'Unknown';
  let browserVersion = '';
  let os = 'Unknown';

  if (ua.includes('Firefox/')) {
    browserName = 'Firefox';
    browserVersion = ua.split('Firefox/')[1]?.split(' ')[0] || '';
  } else if (ua.includes('Edg/')) {
    browserName = 'Edge';
    browserVersion = ua.split('Edg/')[1]?.split(' ')[0] || '';
  } else if (ua.includes('Chrome/')) {
    browserName = 'Chrome';
    browserVersion = ua.split('Chrome/')[1]?.split(' ')[0] || '';
  } else if (ua.includes('Safari/') && !ua.includes('Chrome')) {
    browserName = 'Safari';
    browserVersion = ua.split('Version/')[1]?.split(' ')[0] || '';
  }

  if (ua.includes('Windows')) os = 'Windows';
  else if (ua.includes('Mac OS')) os = 'macOS';
  else if (ua.includes('Linux')) os = 'Linux';
  else if (ua.includes('Android')) os = 'Android';
  else if (ua.includes('iOS') || ua.includes('iPhone')) os = 'iOS';

  return { browserName, browserVersion, os };
}

function buildPayload(error: StructuredError): ErrorReportPayload {
  const { browserName, browserVersion, os } = parseBrowserInfo();

  return {
    severity: (error.severity || 'medium').toUpperCase(),
    category: error.category || 'UNKNOWN',
    message: (error.message || 'Unknown error').slice(0, 2000),
    stackTrace: error.stack?.slice(0, 8000),
    url: (error.context?.url || window.location.href)?.slice(0, 2000),
    component: error.context?.component?.slice(0, 500),
    browser: navigator.userAgent.slice(0, 500),
    browserName,
    browserVersion,
    os,
    screenResolution: `${window.screen.width}x${window.screen.height}`,
    // username intentionally not sent: the backend attributes reports to the
    // authenticated user from the token (audit WP-14).
    tenantName: error.context?.tenantId,
    additionalData: error.context?.action
      ? JSON.stringify({ action: error.context.action }).slice(0, 4000)
      : undefined,
  };
}

async function sendToBackend(payload: ErrorReportPayload): Promise<boolean> {
  // Endpoint requires authentication (audit WP-14). Without a token the report
  // stays queued and is flushed after the next successful login.
  const token = accessTokenStore.get();
  if (!token) {
    return false;
  }
  try {
    const response = await fetch(`${getApiBaseUrl()}/api/client-errors`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    });
    return response.ok || response.status === 429; // 429 = rate limited, don't retry
  } catch {
    return false;
  }
}

// --- localStorage queue for offline retry ---

function loadQueue(): ErrorReportPayload[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveQueue(queue: ErrorReportPayload[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(queue.slice(0, MAX_QUEUE_SIZE)));
  } catch {
    // localStorage full or unavailable — discard silently
  }
}

function enqueue(payload: ErrorReportPayload): void {
  const queue = loadQueue();
  queue.push(payload);
  saveQueue(queue);
}

async function flushQueue(): Promise<void> {
  const queue = loadQueue();
  if (queue.length === 0) return;

  const remaining: ErrorReportPayload[] = [];
  for (const payload of queue) {
    const sent = await sendToBackend(payload);
    if (!sent) {
      remaining.push(payload);
    }
  }
  saveQueue(remaining);
}

// --- Public API ---

/**
 * Report a structured error to the backend.
 * Respects per-session limit and queues for retry on failure.
 */
export async function reportError(error: StructuredError): Promise<void> {
  // Only report errors and critical (skip warnings/info)
  const severity = (error.severity || '').toLowerCase();
  if (severity !== 'error' && severity !== 'high' && severity !== 'critical') {
    return;
  }

  if (sessionReportCount >= MAX_REPORTS_PER_SESSION) {
    return;
  }
  sessionReportCount++;

  const payload = buildPayload(error);
  const sent = await sendToBackend(payload);
  if (!sent) {
    enqueue(payload);
  }
}

/**
 * Start the background queue flush interval.
 * Call once at app startup.
 */
export function startErrorReporter(): void {
  // Flush any queued errors from previous session
  flushQueue();

  if (!flushTimerId) {
    flushTimerId = setInterval(flushQueue, QUEUE_FLUSH_INTERVAL_MS);
  }
}
